import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from '../../contexts/SettingsContext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CustomList, Comic } from '../../types';
import { 
  PencilIcon, 
  TrashIcon, 
  GlobeAltIcon, 
  LockClosedIcon,
  ListBulletIcon,
  FunnelIcon,
  TagIcon,
  EyeIcon,
  DocumentDuplicateIcon
} from '@heroicons/react/24/outline';
import { useWebroot } from '../../hooks';
import { getPublicListUrl } from '../../utils';
import ListItemCard from '../cards/ListItemCard';
import { reorderListComics } from '../../api';

interface ListViewProps {
  list: CustomList;
  onEditClick: (list: CustomList) => void;
  onDeleteClick: (list: CustomList) => void;
  onCopyClick?: (list: CustomList) => void;
  onComicClick: (comic: Comic) => void;
  onListUpdate?: () => void;
}

const ListView: React.FC<ListViewProps> = ({
  list,
  onEditClick,
  onDeleteClick,
  onCopyClick,
  onComicClick,
  onListUpdate,
}) => {
  const { t } = useTranslation();
  const { webroot } = useWebroot();
  const { settings } = useSettings();
  const [showDetails, setShowDetails] = useState(false);
  const [comics, setComics] = useState<Comic[]>(list.comics);
  const [isReordering, setIsReordering] = useState(false);

  // Get showAdultContent from settings context, fallback to localStorage for unauthenticated users
  const showAdultContent = settings?.show_adult_content ?? (localStorage.getItem('showAdultContent') === 'true');

  // Update comics when list changes
  useEffect(() => {
    setComics(list.comics);
  }, [list.comics]);

  const filteredComics = showAdultContent ? comics : comics.filter(comic => !comic.is_adult_content);

  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 500,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = filteredComics.findIndex(comic => comic.id.toString() === active.id);
    const newIndex = filteredComics.findIndex(comic => comic.id.toString() === over.id);
    
    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    const newFilteredComics = arrayMove(filteredComics, oldIndex, newIndex);
    setIsReordering(true);

    // Scroll to the dropped item after a short delay to allow DOM update
    const scrollToDroppedItem = () => {
      setTimeout(() => {
        const droppedElement = document.querySelector(`[data-id="${active.id}"]`);
        if (droppedElement) {
          droppedElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
          });
        }
      }, 100);
    };

    try {
      // When filtering is active, preserve the positions of non-visible comics
      const isFiltered = !showAdultContent && comics.some(comic => comic.is_adult_content);
      
      if (isFiltered) {
        // Create a working copy of all comics to maintain correct ordering
        const allComics = [...comics];
        const draggedComicId = parseInt(active.id);
        const targetComicId = parseInt(over.id);
        
        // Find positions in the full list
        const draggedIndexInFull = allComics.findIndex(c => c.id === draggedComicId);
        const targetIndexInFull = allComics.findIndex(c => c.id === targetComicId);
        
        if (draggedIndexInFull !== -1 && targetIndexInFull !== -1) {
          // Remove the dragged comic from its current position
          const [draggedComic] = allComics.splice(draggedIndexInFull, 1);
          
          // Calculate the new insertion position
          // After removing draggedComic, targetIndexInFull shifts if it's after the removed item
          const adjustedTargetIndex = draggedIndexInFull < targetIndexInFull ? targetIndexInFull - 1 : targetIndexInFull;
          const insertIndex = oldIndex < newIndex ? adjustedTargetIndex + 1 : adjustedTargetIndex;
          
          // Insert the dragged comic at the new position
          allComics.splice(insertIndex, 0, draggedComic);
          
          // Update local state with the new complete order
          setComics(allComics);
          
          // Create reorder data for all comics with their new positions
          const reorderData = {
            comic_orders: allComics.map((comic, index) => ({
              comic_id: comic.id,
              order_index: index
            }))
          };
          
          await reorderListComics(list.id, reorderData);
        }
      } else {
        // When no filtering is active, use the simpler approach
        setComics(newFilteredComics);
        
        const reorderData = {
          comic_orders: newFilteredComics.map((comic, index) => ({
            comic_id: comic.id,
            order_index: index
          }))
        };
        
        await reorderListComics(list.id, reorderData);
      }
      
      if (onListUpdate) {
        onListUpdate();
      }
      
      // Scroll to the dropped item
      scrollToDroppedItem();
    } catch (error) {
      
      // Revert the order on error
      setComics(list.comics);
    } finally {
      setIsReordering(false);
    }
  };

  const formatFilterCriteria = () => {
    if (!list.filter_criteria) return null;
    
    const criteria = [];
    
    if (list.filter_criteria.include_tags.length > 0) {
      criteria.push(`${t('common.include')} tags: ${list.filter_criteria.include_tags.length}`);
    }
    if (list.filter_criteria.exclude_tags.length > 0) {
      criteria.push(`${t('common.exclude')} tags: ${list.filter_criteria.exclude_tags.length}`);
    }
    if (list.filter_criteria.include_statuses.length > 0) {
      criteria.push(`${t('common.include')}: ${list.filter_criteria.include_statuses.join(', ')}`);
    }
    if (list.filter_criteria.exclude_statuses.length > 0) {
      criteria.push(`${t('common.exclude')}: ${list.filter_criteria.exclude_statuses.join(', ')}`);
    }
    if (list.filter_criteria.is_adult_content !== undefined) {
      criteria.push(`${list.filter_criteria.is_adult_content ? t('list.adult') : t('list.safe')} only`);
    }
    
    return criteria;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* List Header */}
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                {list.name}
              </h2>
              <div className="flex items-center space-x-2">
                {list.list_type === 'dynamic' ? (
                  <div className="flex items-center space-x-1 text-blue-600 dark:text-blue-400">
                    <FunnelIcon className="h-4 w-4" />
                    <span className="text-xs font-medium">{t('list.dynamic')}</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1 text-gray-600 dark:text-gray-300">
                    <ListBulletIcon className="h-4 w-4" />
                    <span className="text-xs font-medium">{t('list.manual')}</span>
                  </div>
                )}
                {list.is_public ? (
                  <a
                    href={list.share_token ? getPublicListUrl(list.share_token, webroot) : '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-1 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors cursor-pointer"
                    title={t('list.openPublicView')}
                  >
                    <GlobeAltIcon className="h-4 w-4" />
                    <span className="text-xs font-medium">{t('list.public')}</span>
                  </a>
                ) : (
                  <div className="flex items-center space-x-1 text-gray-500 dark:text-gray-400">
                    <LockClosedIcon className="h-4 w-4" />
                    <span className="text-xs font-medium">{t('list.private')}</span>
                  </div>
                )}
              </div>
            </div>
            
            {list.description && (
              <p className="text-gray-600 dark:text-gray-300 mb-3">{list.description}</p>
            )}
            
            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
              <span>{filteredComics.length} {filteredComics.length === 1 ? t('common.comic') : t('common.comics')}</span>
              <span>{t('common.created')} {new Date(list.created_at).toLocaleDateString()}</span>
              <span>{t('common.updated')} {new Date(list.updated_at).toLocaleDateString()}</span>
            </div>

            {/* Filter Criteria Preview */}
            {list.list_type === 'dynamic' && (
              <div className="mt-3">
                <button
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex items-center space-x-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                >
                  <EyeIcon className="h-4 w-4" />
                  <span>{showDetails ? t('list.hideFilters') : t('list.showFilters')} {t('list.filterDetails')}</span>
                </button>
                
                {showDetails && (
                  <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-md">
                    <div className="text-sm text-gray-600 dark:text-gray-300">
                      {formatFilterCriteria()?.length ? (
                        <ul className="space-y-1">
                          {formatFilterCriteria()?.map((criterion, index) => (
                            <li key={index} className="flex items-center space-x-2">
                              <TagIcon className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                              <span>{criterion}</span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span>{t('list.noFiltersApplied')}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            <button
              onClick={() => onEditClick(list)}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              title={t('list.editList')}
            >
              <PencilIcon className="h-5 w-5" />
            </button>
            {onCopyClick && (
              <button
                onClick={() => onCopyClick(list)}
                className="p-2 text-gray-400 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                title={t('list.copyList')}
              >
                <DocumentDuplicateIcon className="h-5 w-5" />
              </button>
            )}
            <button
              onClick={() => onDeleteClick(list)}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
              title={t('list.deleteList')}
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Comics List */}
      <div className="p-6">
        {filteredComics.length > 0 ? (
          <div className="space-y-3">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={filteredComics.map(comic => comic.id.toString())}
                strategy={verticalListSortingStrategy}
              >
                {filteredComics.map((comic) => (
                  <div key={comic.id} data-id={comic.id}>
                    <ListItemCard
                      comic={comic}
                      onComicClick={onComicClick}
                      isDragDisabled={isReordering}
                    />
                  </div>
                ))}
              </SortableContext>
            </DndContext>
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-500 mb-4">
              {list.list_type === 'dynamic' ? (
                <FunnelIcon className="h-12 w-12 mx-auto" />
              ) : (
                <ListBulletIcon className="h-12 w-12 mx-auto" />
              )}
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">{t('list.noComicsInList')}</h3>
            <p className="text-gray-500 dark:text-gray-400">
              {list.list_type === 'dynamic' 
                ? t('list.noComicsMatchFilter')
                : t('list.addComicsToGetStarted')
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListView;